import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

function AnimatedNumber({ value, testid }) {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);

  useEffect(() => {
    const from = prev.current;
    const to = value;
    prev.current = value;
    const start = performance.now();
    const dur = 700;
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return <span data-testid={testid}>{fmt(display)}</span>;
}

const Slider = ({ label, value, min, max, step = 1, onChange, prefix = "", testid }) => (
  <div>
    <div className="flex justify-between items-baseline mb-3">
      <label className="text-sm text-white/70" htmlFor={testid}>{label}</label>
      <span className="font-display font-semibold text-lg text-[#A7D7A0]" data-testid={`${testid}-value`}>
        {prefix}{value}
      </span>
    </div>
    <input
      id={testid}
      data-testid={testid}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-1 appearance-none bg-white/15 rounded-full accent-[#A7D7A0] cursor-pointer"
    />
  </div>
);

export default function Calculator() {
  const navigate = useNavigate();
  const [holes, setHoles] = useState(9);
  const [rounds, setRounds] = useState(20);
  const [rate, setRate] = useState(65);
  const [nights, setNights] = useState(12);

  const monthly = useMemo(() => rounds * rate * nights, [rounds, rate, nights]);
  const annual = monthly * 12;

  const requestQuote = () => {
    navigate("/quote", {
      state: { calculator: { holes, rounds_per_night: rounds, revenue_per_round: rate, nights_per_month: nights } },
    });
  };

  return (
    <section className="py-24 sm:py-32 lg:py-40 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto" data-testid="calculator-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Interactive Estimator</Eyebrow>
            <h2 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-tight">
              What Could Night Golf Mean for Your Revenue?
            </h2>
            <p className="mt-8 text-muted-green text-base leading-relaxed max-w-md">
              Model the potential of evening play at your facility. Adjust the inputs — the
              estimate updates instantly.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 space-y-8">
              <Slider label="Holes to illuminate" value={holes} min={1} max={27} onChange={setHoles} testid="calc-holes-slider" />
              <Slider label="Additional rounds per night" value={rounds} min={1} max={120} onChange={setRounds} testid="calc-rounds-slider" />
              <Slider label="Average revenue per round" value={rate} min={10} max={300} step={5} onChange={setRate} prefix="$" testid="calc-rate-slider" />
              <Slider label="Operating nights per month" value={nights} min={1} max={31} onChange={setNights} testid="calc-nights-slider" />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.2}>
            <div className="bg-card-dark border hairline rounded-md p-8 sm:p-12 relative overflow-hidden grain">
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#326B46]/20 rounded-full blur-3xl pointer-events-none" />
              <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-white/40 mb-4">
                Estimated Additional Monthly Gross Revenue
              </p>
              <p className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">
                <AnimatedNumber value={monthly} testid="calc-monthly-result" />
              </p>

              <div className="mt-10 pt-10 border-t hairline">
                <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-white/40 mb-4">
                  Estimated Additional Annual Gross Revenue
                </p>
                <p className="font-display font-bold text-3xl sm:text-4xl text-[#A7D7A0] tracking-tight">
                  <AnimatedNumber value={annual} testid="calc-annual-result" />
                </p>
              </div>

              <p className="mt-10 text-xs text-white/40 leading-relaxed max-w-lg" data-testid="calc-disclaimer">
                Illustrative gross revenue estimates only. Actual results depend on demand,
                operating costs, seasonality, course capacity, and other factors. Estimates
                are not guaranteed.
              </p>

              <button
                data-testid="calc-request-quote-btn"
                onClick={requestQuote}
                className="mt-8 inline-flex items-center gap-3 bg-[#A7D7A0] text-[#101214] font-semibold px-7 py-3.5 rounded-sm hover:bg-white transition-colors duration-300 group"
              >
                Request a Lighting Feasibility Quote
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
